import React, { useCallback, useEffect, useReducer, useState } from "react"
import type { SalvarCuponsPayload } from "../../../services/cupomEstabelecimento"
import {
    obterCuponsEstabelecimento,
    salvarCuponsEstabelecimento,
} from "../../../services/cupomEstabelecimento"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import type { CupomItem } from "../models/cupomEstabelecimentoFornecedorModel"
import { CupomEstabelecimentoFornecedorInicial } from "../models/cupomEstabelecimentoFornecedorModel"
import { CupomEstabelecimentoFornecedorActionsTypes } from "./cupomEstabelecimentoFornecedorActions"
import { CupomEstabelecimentoFornecedorContext } from "./CupomEstabelecimentoFornecedorContext"
import { cupomEstabelecimentoFornecedorReducer } from "./cupomEstabelecimentoFornecedorReducer"

export function CupomEstabelecimentoFornecedorContextProvider({
    children,
}: {
    children: React.ReactNode
}) {
    const [dados, dispatch] = useReducer(
        cupomEstabelecimentoFornecedorReducer,
        CupomEstabelecimentoFornecedorInicial
    )
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)

    const carregar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await obterCuponsEstabelecimento(autorizacao)
            if ("retornoErro" in response) {
                setErroCarregamento(response.mensagem || "Erro ao carregar cupons")
            } else {
                setErroCarregamento(null)
                const itens = response.map((c, i) => ({
                    localId: i + 1,
                    id: c.id,
                    cupomId: c.cupomId,
                    nomeCupom: c.nomeCupom,
                    valor: c.valor,
                    percentual: c.percentual,
                    quantidadePorCliente: c.quantidadePorCliente,
                    quantidadeMaxima: c.quantidadeMaxima,
                    valorMinimoPedido: c.valorMinimoPedido,
                    maximoDesconto: c.maximoDesconto,
                    apenasPrimeiraCompra: c.apenasPrimeiraCompra,
                    automatico: c.automatico,
                    ativo: c.ativo,
                    freteGratis: c.freteGratis,
                    appSiteTodos: c.appSiteTodos,
                    validadeDias: c.validadeDias,
                    fimCupomTimestamp: c.fimCupomTimestamp,
                    visivelTelaInicial: c.visivelTelaInicial,
                    horarioAlmoco: c.horarioAlmoco,
                    exigirFacebookParaDesconto: c.exigirFacebookParaDesconto,
                    exigirEnderecoUnicoParaDesconto: c.exigirEnderecoUnicoParaDesconto,
                    exigirCpfParaDesconto: c.exigirCpfParaDesconto,
                    telefoneCliente: c.telefoneCliente,
                    descricao: c.descricao ?? "",
                    olharListaClientes: c.olharListaClientes,
                    quantidadeUtilizada: c.quantidadeUtilizada,
                }))
                dispatch({
                    type: CupomEstabelecimentoFornecedorActionsTypes.CARREGAR,
                    payload: itens,
                })
            }
        } catch {
            setErroCarregamento("Erro ao carregar cupons")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        carregar()
    }, [])

    const enviar = async (payload: SalvarCuponsPayload) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) throw new Error("Sessão expirada. Faça login novamente.")
        try {
            setLoading(true)
            const response = await salvarCuponsEstabelecimento(autorizacao, payload)
            if ("retornoErro" in response) {
                throw new Error(response.mensagem)
            }
            return response
        } finally {
            setLoading(false)
        }
    }

    // O endpoint faz upsert dos cupons enviados + delete dos deletarIds,
    // então cada cupom é salvo/excluído individualmente a partir do diálogo.
    const salvarCupom = useCallback(async (c: CupomItem) => {
        const response = await enviar({
            cupons: [
                {
                    id: c.id,
                    cupomId: c.cupomId,
                    nomeCupom: c.nomeCupom,
                    valor: c.valor,
                    percentual: c.percentual,
                    quantidadePorCliente: c.quantidadePorCliente,
                    quantidadeMaxima: c.quantidadeMaxima,
                    valorMinimoPedido: c.valorMinimoPedido,
                    maximoDesconto: c.maximoDesconto,
                    apenasPrimeiraCompra: c.apenasPrimeiraCompra,
                    automatico: c.automatico,
                    ativo: c.ativo,
                    freteGratis: c.freteGratis,
                    appSiteTodos: c.appSiteTodos,
                    validadeDias: c.validadeDias,
                    fimCupomTimestamp: c.fimCupomTimestamp,
                    visivelTelaInicial: c.visivelTelaInicial,
                    horarioAlmoco: c.horarioAlmoco,
                    // Exigir Facebook foi descontinuado: sempre false.
                    exigirFacebookParaDesconto: false,
                    exigirEnderecoUnicoParaDesconto: c.exigirEnderecoUnicoParaDesconto,
                    exigirCpfParaDesconto: c.exigirCpfParaDesconto,
                    telefoneCliente: c.telefoneCliente,
                    descricao: c.descricao,
                    olharListaClientes: c.olharListaClientes,
                },
            ],
            deletarIds: [],
        })
        const salvo: CupomItem = {
            ...c,
            exigirFacebookParaDesconto: false,
            id: response[0]?.id ?? c.id,
            cupomId: response[0]?.cupomId ?? c.cupomId,
        }
        dispatch({
            type: CupomEstabelecimentoFornecedorActionsTypes.CUPOM_SALVO,
            payload: salvo,
        })
        return salvo
    }, [])

    const excluirCupom = useCallback(async (c: CupomItem) => {
        if (c.id !== null && c.id > 0) {
            await enviar({ cupons: [], deletarIds: [c.id] })
        }
        dispatch({
            type: CupomEstabelecimentoFornecedorActionsTypes.CUPOM_EXCLUIDO,
            payload: { localId: c.localId },
        })
    }, [])

    return (
        <CupomEstabelecimentoFornecedorContext.Provider
            value={{ dados, dispatch, salvarCupom, excluirCupom, erroCarregamento }}
        >
            <Loading />
            {children}
        </CupomEstabelecimentoFornecedorContext.Provider>
    )
}
